import React, { useCallback, useState, useRef, useMemo, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { Button } from "devextreme-react/button";
import AccordionReact from "devextreme-react/accordion";
import Toolbar, { Item } from "devextreme-react/toolbar";
import {
    Column,
    DataGrid,
    GroupPanel,
    Grouping,
    FilterRow,
    HeaderFilter,
    FilterPanel,
    Sorting,
    Paging,
    Selection,
    Toolbar as GridToolbar,
    Item as GridToolbarItem,
    Export,
    Pager,
    ColumnChooser,
    ColumnChooserSearch,
    StateStoring,
    Lookup,
} from "devextreme-react/data-grid";
import FormField from "../../dynamicFormBuilder/FormField";
import { GridLayout } from "../../common/GridLayout";
import GridLayoutManager from "../../gridLayoutCustomization/GridLayoutManager";
import { GetTransactionHeaderGridData } from "../itemManagement/services/ItemManagementService";
import { GetLookupStaticData } from "../../om/services/SalesOrderService";
import { TRANSACTION_TYPES, STAGES, STATUSES, toSelectOptions } from "../itemManagement/transactionsTab/codeLists";
import TransactionSearchLayout from "./SearchLayout";

// FIND screen for Delphi uIMD0019 "IM Transaction Entry" (browse of IMTRANSACTIONHEADER).
// Follows the OM Sales Order Data Entry List.js pattern (accordion search panel + results grid),
// and reuses the existing IM transaction-header search schema, coded-field legends, and
// GetTransactionHeaders endpoint (same IMTRANSACTIONHEADER table as Item Management / IMD0046).
const searchpanel = [{ ID: 1, Title: "Search Filters" }];
const allowedPageSizes = [10, 20, 100, 500, "all"];
const money = { type: "fixedPoint", precision: 2, useGrouping: true };
const RANGE10 = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

// Seed one formFields key per search-schema field so controlled inputs stay controlled.
const buildInitialFields = () => {
    const init = {};
    TransactionSearchLayout.forEach((g) => g.fields.forEach((f) => { init[f.name] = f.type === "checkbox" ? false : ""; }));
    // Delphi opens the Find screen with "Open Status Only" ticked.
    init.OpenStatusOnly = true;
    return init;
};

function List(props) {
    const accessToken = props.oidc?.user?.access_token;
    const currentUser = props.oidc?.user;
    const dynamicFormSetting = props.oidc?.user?.profile?.DYNAMICFORMSETTING;
    const history = useHistory();

    const gridKey = "3f7a1c2e-9b84-4d6a-ae21-0019fddca7b1";
    const dataGrid = useRef(null);
    const rowId = useRef("");
    const formRefs = useRef({});

    const [formFields, setformFields] = useState(buildInitialFields());
    const [dataSource, setDataSource] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]);
    const [searchPanelStatus, setSearchPanelStatus] = useState(false);
    const [isEditButtonDisabled, setIsEditButtonDisabled] = useState(true);
    const [currentLayout, setCurrentLayout] = useState("Default");
    const [forceRender, setForceRender] = useState(false);

    const saveState = useCallback((state) => { GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state, currentLayout); }, [accessToken, currentLayout]);
    const loadState = useCallback(() => { return GridLayout.sendStorageRequest(gridKey, "GET", accessToken, null, currentLayout); }, [accessToken, currentLayout]);

    // Delphi uIMD0019 browse shows not-yet-committed transactions only
    // (RecordType R/Z, STATUS in O/H/L/N) — GetTransactionHeaders `historical=false` branch.
    const loadData = useCallback((fields) => {
        setDataSource(GetTransactionHeaderGridData(accessToken, false, fields));
    }, [accessToken]);

    // Initial load on mount.
    useEffect(() => {
        loadData(formFields);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken]);

    const selectionChanged = useCallback((e) => {
        let newItems = [...selectedItems];
        e.removedItems.forEach((item) => { const idx = newItems.indexOf(item); if (idx >= 0) newItems.splice(idx, 1); });
        if (e.addedItems.length) newItems = [...newItems, ...e.addedItems];
        setSelectedItems(newItems);
        setSearchPanelStatus((prev) => !prev);
    }, [selectedItems]);

    const handleSearch = (e) => { e.event.stopPropagation(); loadData(formFields); };
    const handleReset = (e) => { e.event.stopPropagation(); const init = buildInitialFields(); setformFields(init); loadData(init); };
    const handleFieldChange = useCallback(() => {}, []);

    // Coded search fields render as descriptive dropdowns (rule 3) — options injected after the form loads.
    const handleFormLoad = useCallback(() => {
        GetLookupStaticData("TransactionType", toSelectOptions(TRANSACTION_TYPES), formRefs, setForceRender, forceRender);
        GetLookupStaticData("Stage", toSelectOptions(STAGES), formRefs, setForceRender, forceRender);
        GetLookupStaticData("Status", toSelectOptions(STATUSES), formRefs, setForceRender, forceRender);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSelectionChangedGrid = ({ selectedRowsData }) => {
        const row = selectedRowsData && selectedRowsData.length === 1 ? selectedRowsData[0] : null;
        rowId.current = row ? row.rowid : "";
        setIsEditButtonDisabled(!row);
    };

    const handleEditClick = () => {
        if (!rowId.current) return;
        history.push("/im/transaction/edit-transaction?id=" + rowId.current);
    };
    const handleCreateClick = () => { history.push("/im/transaction/add-transaction"); };

    const pager = useMemo(() => ({ visible: true, showPageSizeSelector: true, allowedPageSizes, showNavigationButtons: true }), []);

    return (
        <div>
            <AccordionReact
                dataSource={searchpanel}
                collapsible={true}
                multiple={false}
                animationDuration={300}
                selectedItems={selectedItems}
                onSelectionChanged={(e) => { if (e.currentTarget === e.target) selectionChanged(e); }}
                itemTitleRender={() => (
                    <Toolbar className="accordian-toolbar">
                        <Item location="before">
                            <div className="title">
                                <h6 style={{ color: window._env_.Color2 }}>Enter Transaction</h6>
                            </div>
                        </Item>
                        <Item location="after">
                            <Button className="accordianButton" onClick={handleSearch} text="Search" type="default" />
                        </Item>
                        <Item location="after">
                            <Button className="accordianButton" onClick={handleReset} text="Reset" />
                        </Item>
                    </Toolbar>
                )}
                itemRender={() => (
                    <div>
                        <FormField
                            currrentUser={currentUser}
                            layoutId="0019d019-1f34-4b56-9a21-0019aabbccdd"
                            layoutName="IMTransactionEntrySearchForm"
                            defaultFormSchema={TransactionSearchLayout}
                            formFields={formFields}
                            setformFields={setformFields}
                            handleFieldValueChange={handleFieldChange}
                            DYNAMICFORMSETTING={dynamicFormSetting}
                            formRefs={formRefs}
                            onFormLoad={handleFormLoad}
                            noSpacingBeforeFirstRow={true}
                        />
                    </div>
                )}
                id="accordion-container"
                className="accordion-height"
            />
            <DataGrid
                ref={dataGrid}
                dataSource={dataSource}
                remoteOperations={true}
                showBorders={true}
                hoverStateEnabled={true}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={true}
                className={searchPanelStatus ? "GridHeightAccordianOpen" : "GridHeightAccordianClose"}
                onSelectionChanged={onSelectionChangedGrid}
                onExporting={(e) => { if (window.handleDevExtremeDataGridExport) window.handleDevExtremeDataGridExport(e); }}
            >
                <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                    <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: "Search column" }} />
                </ColumnChooser>
                <StateStoring enabled={true} type="custom" customLoad={loadState} storageKey={gridKey} customSave={saveState} />
                <Export enabled={true} allowExportSelectedData={true} />
                {/* Checkbox selection, matching WM Transaction Data Entry
                    (wm/components/transactions/dataentry/list.js) so record selection behaves the
                    same across the data-entry listings. Edit still requires exactly one row —
                    onSelectionChangedGrid disables it for any other count. */}
                <Selection mode="multiple" />
                <Sorting mode="multiple" />
                <GroupPanel visible={true} />
                <Grouping autoExpandAll={false} />
                <FilterRow visible={true} />
                <HeaderFilter visible={true} />
                <FilterPanel visible={true} />
                <Paging defaultPageSize={20} />
                <Pager {...pager} />

                {/* Delphi grid columns (frmIMD0019 header grid), same captions. Technical key hidden (rule 8),
                    Record # human column (rule 10), coded fields as descriptive lookups (rule 3). */}
                <Column dataField="rowid" caption="Row Id" visible={false} showInColumnChooser={false} />
                {/* Default-visible set matches the Delphi Find grid: KEYSEQUENCE, TRANSACTIONTYPE,
                    TRANSACTIONDATE, REFERENCE, STATUS, CREATIONDATETIME, STAGE. Everything else is
                    available through the column chooser. */}
                <Column dataField="recordNumber" caption="Record #" dataType="string" />
                <Column dataField="transactionType" caption="Type">
                    <Lookup dataSource={TRANSACTION_TYPES} valueExpr="code" displayExpr="label" />
                </Column>
                <Column dataField="transactionDate" caption="Date" dataType="datetime" format="MM/dd/yyyy hh:mm a" />
                <Column dataField="reference" caption="Reference #" dataType="string" />
                <Column dataField="status" caption="Status">
                    <Lookup dataSource={STATUSES} valueExpr="code" displayExpr="label" />
                </Column>
                <Column dataField="creationDateTime" caption="Creation Date" dataType="datetime" format="MM/dd/yyyy hh:mm a" />
                <Column dataField="stage" caption="Stage">
                    <Lookup dataSource={STAGES} valueExpr="code" displayExpr="label" />
                </Column>

                {/* Available but not shown by default. TotalAmount / TotalQuantity are gone entirely:
                    they are not Delphi Find columns and cost a correlated aggregate per row. */}
                <Column dataField="description" caption="Description" dataType="string" visible={false} />
                <Column dataField="profileCode" caption="Profile" dataType="string" visible={false} />
                <Column dataField="profileDescription" caption="Profile Description" dataType="string" visible={false} />
                <Column dataField="creationUserId" caption="Created By" dataType="string" visible={false} />
                <Column dataField="lastUpdateDateTime" caption="Last Updated" dataType="datetime" format="MM/dd/yyyy hh:mm a" visible={false} />
                <Column dataField="lastUpdateUserId" caption="Last Updated By" dataType="string" visible={false} />
                <Column dataField="commitDate" caption="Commit Date" dataType="datetime" format="MM/dd/yyyy hh:mm a" visible={false} />
                <Column dataField="importFileName" caption="Name" dataType="string" visible={false} />
                <Column dataField="comment" caption="Comment" dataType="string" visible={false} />
                <Column dataField="userDefinedComment" caption="User Comment" dataType="string" visible={false} />
                <Column dataField="fiscalYear" caption="Fiscal Year" dataType="number" visible={false} />
                <Column dataField="fiscalPeriod" caption="Fiscal Period" dataType="number" visible={false} />
                {RANGE10.map((i) => (<Column key={`code${i}`} dataField={`code${i}`} caption={`Code - ${i}`} dataType="string" visible={false} />))}
                {RANGE10.map((i) => (<Column key={`reference${i}`} dataField={`reference${i}`} caption={`Reference - ${i}`} dataType="string" visible={false} />))}
                {RANGE10.map((i) => (<Column key={`date${i}`} dataField={`date${i}`} caption={`Date - ${i}`} dataType="date" format="MM/dd/yyyy" visible={false} />))}
                {RANGE10.map((i) => (<Column key={`quantity${i}`} dataField={`quantity${i}`} caption={`Quantity - ${i}`} dataType="number" format={money} visible={false} />))}

                <GridToolbar>
                    <GridToolbarItem location="before">
                        <Button disabled={isEditButtonDisabled} onClick={handleEditClick} type="default" text="Edit" />
                    </GridToolbarItem>
                    <GridToolbarItem location="before">
                        <Button onClick={handleCreateClick} type="default" className="gridTopButton" text="Create" />
                    </GridToolbarItem>
                    <GridToolbarItem location="after">
                        <GridLayoutManager gridKey={gridKey} dataGrid={dataGrid} setCurrentLayout={setCurrentLayout} />
                    </GridToolbarItem>
                    <GridToolbarItem name="exportButton" />
                    <GridToolbarItem name="columnChooserButton" />
                </GridToolbar>
            </DataGrid>
        </div>
    );
}

function mapStateToProps(state) {
    return { oidc: state.oidc };
}

export default connect(mapStateToProps)(List);
